import {
  HHAlert,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { useEffect, useState } from 'react';
import { CursorPagination } from '../components/cursor-pagination';
import { FilterBar } from '../components/filter-bar';
import { IngestionsTable } from '../components/ingestions-table';
import { PageHeader } from '../components/page-header';
import { ThroughputPanel } from '../components/throughput-panel';
import { usePermissions } from '../context/permissions-context';
import { FileProcessingStatus } from '../domain/enums';
import { useDataProviders } from '../hooks/use-data-providers';
import { useIngestionSummary } from '../hooks/use-ingestion-summary';
import {
  DEFAULT_POLL_INTERVAL_MS,
  useIngestions,
} from '../hooks/use-ingestions';
import { DEFAULT_INGESTION_FILTERS, IngestionFilters } from '../types';

/** Exported so tests can advance timers by exactly the debounce window. */
export const SEARCH_DEBOUNCE_MS = 300;

export const INGESTIONS_PAGE_TEST_ID = 'partner-data-gateway-ingestions-page';
export const INGESTIONS_PAGE_NO_ACCESS_TEST_ID =
  'partner-data-gateway-ingestions-page-no-access';

const POLL_INTERVAL_SECONDS = DEFAULT_POLL_INTERVAL_MS / 1000;
export const REFRESH_HINT = `Refreshes every ${POLL_INTERVAL_SECONDS} s while files are in flight`;

export const IngestionsPage = (): JSX.Element => {
  const { canViewIngestions } = usePermissions();

  const [filters, setFilters] = useState<IngestionFilters>(
    DEFAULT_INGESTION_FILTERS,
  );
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, SEARCH_DEBOUNCE_MS);

    return (): void => clearTimeout(timeoutId);
  }, [searchInput]);

  const { stats } = useIngestionSummary();
  const { dataProviders } = useDataProviders();

  // PDGS filters ingestions by a single status. A single-status selection is
  // pushed to the server; a status *group* (e.g. "In flight") is applied
  // client-side to the returned page below.
  const selectedStatuses = filters.statuses;
  const serverStatus =
    selectedStatuses && selectedStatuses.length === 1
      ? selectedStatuses[0]
      : undefined;

  const {
    ingestions,
    isLoading,
    hasError,
    hasNext,
    hasPrevious,
    currentPage,
    goToNext,
    goToPrevious,
  } = useIngestions({
    status: serverStatus,
    dataProvider: filters.dataProvider ?? undefined,
    createdAfter: filters.createdAfter ?? undefined,
    createdBefore: filters.createdBefore ?? undefined,
  });

  const matchesGroupStatus = (status: FileProcessingStatus): boolean =>
    !selectedStatuses ||
    selectedStatuses.length <= 1 ||
    selectedStatuses.includes(status);

  const matchesSearch = (fileName: string): boolean =>
    !debouncedSearch ||
    fileName.toLowerCase().includes(debouncedSearch.toLowerCase());

  const visibleIngestions = ingestions.filter(
    ingestion =>
      matchesGroupStatus(ingestion.status) && matchesSearch(ingestion.fileName),
  );

  // Access control lives on the page, not just the route, so the module stays
  // safe wherever the host mounts it.
  if (!canViewIngestions) {
    return (
      <HHAlert hhVariant="outlined" severity="warning">
        <span data-testid={INGESTIONS_PAGE_NO_ACCESS_TEST_ID}>
          You do not have access to view partner data ingestions.
        </span>
      </HHAlert>
    );
  }

  return (
    // The parent owns horizontal padding so the title, panel and table share
    // one left edge (see pages/CLAUDE.md in billing-tool).
    <HHStack
      flex={1}
      direction="column"
      gap={6}
      paddingX={6}
      paddingY={6}
      data-testid={INGESTIONS_PAGE_TEST_ID}
    >
      <PageHeader
        title="Ingestions"
        subtitle="Every file received from partners, across all providers."
      />

      <ThroughputPanel stats={stats} />

      <IngestionsTable
        ingestions={visibleIngestions}
        isLoading={isLoading}
        hasError={hasError}
        toolbar={
          <FilterBar
            filters={filters}
            onFiltersChange={setFilters}
            dataProviders={dataProviders}
            searchTerm={searchInput}
            onSearchTermChange={setSearchInput}
            trailing={
              <HHTypography
                hhVariant="variant-bypass"
                variant="body2"
                color="text.secondary"
                noWrap
              >
                {REFRESH_HINT}
              </HHTypography>
            }
          />
        }
        footer={
          <CursorPagination
            currentPage={currentPage}
            hasNext={hasNext}
            hasPrevious={hasPrevious}
            onNext={goToNext}
            onPrevious={goToPrevious}
          />
        }
      />
    </HHStack>
  );
};

export default IngestionsPage;
